import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import SafeIcon from '../../common/SafeIcon';
import { 
  FiUsers, FiDownload, FiChevronLeft, FiChevronRight, 
  FiSearch, FiMail, FiUser, FiShield 
} from 'react-icons/fi';
import { motion } from 'framer-motion';

const ITEMS_PER_PAGE = 25;

const UserDirectory = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('profiles_1716182400000')
        .select('*', { count: 'exact' });

      if (searchTerm) {
        query = query.or(`full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }

      const { data, count, error } = await query
        .order('full_name', { ascending: true })
        .range((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE - 1);

      if (error) throw error;
      setUsers(data || []);
      setTotalCount(count || 0);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    const headers = ['Name', 'Email', 'Role', 'Avatar URL', 'Last Updated'];
    const csvContent = [
      headers.join(','),
      ...users.map(u => [
        `"${u.full_name || 'N/A'}"`,
        `"${u.email}"`,
        `"${u.role}"`,
        `"${u.avatar_url || ''}"`,
        `"${new Date(u.updated_at).toLocaleDateString()}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `pod_studio_users_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return (
    <div className="max-w-6xl mx-auto py-8 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h2 className="text-3xl font-serif font-bold text-espresso">User Directory</h2>
          <p className="text-espresso-light/60 text-sm">Monitor and manage access across your workspace.</p>
        </div>
        <button 
          onClick={downloadCSV}
          className="flex items-center gap-2 px-6 py-3 bg-espresso text-white rounded-2xl font-bold text-xs hover:bg-espresso-light transition-all shadow-lg"
        >
          <SafeIcon icon={FiDownload} /> Export CSV
        </button>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-cream-dark overflow-hidden">
        {/* Search Bar */}
        <div className="p-6 border-b border-cream-dark bg-cream/10">
          <div className="relative max-w-md">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-espresso-light/30" />
            <input 
              type="text"
              placeholder="Search by name or email..."
              className="w-full pl-12 pr-4 py-3 bg-white border border-cream-dark rounded-2xl text-sm focus:outline-none focus:border-terracotta transition-all"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-cream/30 border-b border-cream-dark">
                <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-espresso-light/40">User</th>
                <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-espresso-light/40">Contact Info</th>
                <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-espresso-light/40 text-center">Role</th>
                <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-espresso-light/40 text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-dark">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4" colSpan="4">
                      <div className="h-12 bg-cream/50 rounded-xl" />
                    </td>
                  </tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-espresso-light/40 italic">
                    No users found matching your criteria.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <motion.tr 
                    key={user.id} 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-cream/10 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-terracotta/10 border border-terracotta/20 overflow-hidden shadow-inner">
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <SafeIcon icon={FiUser} className="text-terracotta/40 w-5 h-5" />
                            </div>
                          )}
                        </div>
                        <span className="font-bold text-espresso text-sm">
                          {user.full_name || 'Anonymous User'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-xs text-espresso-light">
                        <SafeIcon icon={FiMail} className="w-3 h-3 opacity-30" />
                        {user.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-tighter border ${
                        user.role === 'admin' 
                          ? 'bg-terracotta/10 border-terracotta/30 text-terracotta' 
                          : 'bg-sage/10 border-sage/30 text-sage'
                      }`}>
                        <SafeIcon icon={user.role === 'admin' ? FiShield : FiUser} className="w-2 h-2" />
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-[10px] font-bold text-espresso-light/30 uppercase">
                        Active
                      </span>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-6 bg-cream/5 border-t border-cream-dark flex items-center justify-between">
          <p className="text-[10px] font-bold text-espresso-light/40 uppercase tracking-widest">
            Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount} users
          </p>
          <div className="flex items-center gap-2">
            <button 
              disabled={currentPage === 1 || loading}
              onClick={() => setCurrentPage(p => p - 1)}
              className="p-2 rounded-lg border border-cream-dark hover:bg-white disabled:opacity-30 transition-all"
            >
              <SafeIcon icon={FiChevronLeft} />
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                    currentPage === i + 1 
                      ? 'bg-espresso text-white shadow-md' 
                      : 'hover:bg-white text-espresso-light'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <button 
              disabled={currentPage === totalPages || loading}
              onClick={() => setCurrentPage(p => p + 1)}
              className="p-2 rounded-lg border border-cream-dark hover:bg-white disabled:opacity-30 transition-all"
            >
              <SafeIcon icon={FiChevronRight} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDirectory;